import { fullDateWithDashes } from '~constants';
import { getRangeOfDates } from './getRangeOfDates';
import { format } from 'date-fns';

/**
 * @typedef {Object} PassedStateType
 * @property {Date[] | []} bookedDays - Array of booked dates.
 * @property {Date[] | []} checkoutDates - Array of checkout dates.
 * @property {Date[] | []} checkinDates - Array of check-in dates.
 * @property {Date[] | []} blockedDates - Array of blocked dates.
 */
type PassedAvailabilityStateType = {
    bookedDays: Date[];
    checkoutDates: Date[];
    checkinDates: Date[];
    blockedDates: Date[];
};

enum ModifiersOperation {
    BLOCKED_DATES = 'blockedDates',
    CHECK_OUT = 'checkoutDates',
    CHECK_IN = 'checkinDates'
}

/**
 * Function to filters state after block/unblock operation. Created to avoid refetching availability from backend and manage state on frontend.
 * @function unblockDatesModifiers
 * @param {PassedStateType} state - The current state.
 * @param {Date[] | Date} selectedDateRange - The selected date range.
 * @param {ModifiersOperation} [operation=ModifiersOperation.BLOCKED_DATES] - The operation to perform.
 * @returns {Date[]} - The updated state.
 */
const unblockDatesModifiers = (
    state: PassedAvailabilityStateType,
    selectedDateRange: Date[] | Date,
    operation = ModifiersOperation.BLOCKED_DATES
) => {
    return state[operation].filter(date => {
        return !selectedDateRange
            .toString()
            .includes(format(new Date(date), 'iii MMM dd yyyy'));
    });
};

const datesAlreadyInState = (
    state: Array<string | Date>,
    selectedDateRange: Date[] | Date
) => {
    return state.filter(date =>
        selectedDateRange
            .toString()
            .includes(format(new Date(date), 'iii MMM dd yyyy'))
    );
};

const blockDatesModifiers = (selectedDateRange: Date[], otherDates: Date[]) => {
    return selectedDateRange.filter(
        date =>
            !otherDates
                .toString()
                .includes(format(new Date(date), 'iii MMM dd yyyy'))
    );
};

const checkoutContainsCheckins = (checkouts: Date[], checkins: Date[]) => {
    return checkouts.filter(date =>
        checkins.toString().includes(format(new Date(date), 'iii MMM dd yyyy'))
    );
};

const selectedRangeContainsCheckinsCheckouts = (
    selectedRange: Date[],
    checkinsCheckouts: Date[]
) => {
    return checkinsCheckouts.filter(date =>
        selectedRange
            .toString()
            .includes(format(new Date(date), 'iii MMM dd yyyy'))
    );
};

/**
 * Function to block or unblock date availability.
 * @function blockUnblockDateAvailability
 * @param {Date} dateFrom - The start date.
 * @param {PassedStateType} passedAvailabilityState - The current availability state.
 * @param {Date} [dateTo] - The end date.
 * @param {boolean} [isDateInBlockedDates=false] - Flag to check if date is in blocked dates.
 * @returns {Object} - The updated data.
 */
export const blockUnblockDateAvailability = (
    dateFrom: Date,
    passedAvailabilityState: PassedAvailabilityStateType,
    dateTo?: Date,
    isDateInBlockedDates = false,
    isStr = false
) => {
    const rangeOfDates = getRangeOfDates(dateFrom, dateTo ?? dateFrom);
    const modifiersForDates: {
        checkOuts: Date;
        checkIns: Date;
    } = {
        checkOuts: dateTo ?? dateFrom,
        checkIns: dateFrom
    };
    const availabilityWithUnblockDates = unblockDatesModifiers(
        passedAvailabilityState,
        rangeOfDates
    );

    const availabilityWithUnblockedCheckoutDates = unblockDatesModifiers(
        passedAvailabilityState,
        modifiersForDates.checkOuts,
        ModifiersOperation.CHECK_OUT
    );

    const availabilityWithUnblockedCheckinDates = unblockDatesModifiers(
        passedAvailabilityState,
        modifiersForDates.checkIns,
        ModifiersOperation.CHECK_IN
    );
    const availabilityWithBlockedDates = blockDatesModifiers(rangeOfDates, [
        modifiersForDates.checkIns,
        modifiersForDates.checkOuts
    ]);
    const mergedBlockedDates = checkoutContainsCheckins(
        [...passedAvailabilityState.checkoutDates, modifiersForDates.checkOuts],
        [...passedAvailabilityState.checkinDates, modifiersForDates.checkIns]
    );
    const blockedDatesInModifiers = selectedRangeContainsCheckinsCheckouts(
        [dateFrom, dateTo ?? dateFrom],
        [
            ...passedAvailabilityState.checkoutDates,
            ...passedAvailabilityState.checkinDates
        ]
    );

    const strUpdatedData = {
        checkoutDates: isDateInBlockedDates
            ? [
                  ...availabilityWithUnblockedCheckoutDates,
                  ...datesAlreadyInState(
                      [
                          ...passedAvailabilityState.blockedDates,
                          ...passedAvailabilityState.bookedDays
                      ],
                      dateFrom
                  )
              ]
            : [
                  ...passedAvailabilityState.checkoutDates,
                  modifiersForDates.checkOuts
              ].filter(
                  date =>
                      ![...mergedBlockedDates, ...blockedDatesInModifiers]
                          .map(date =>
                              format(new Date(date), fullDateWithDashes)
                          )
                          .toString()
                          .includes(format(new Date(date), fullDateWithDashes))
              ),
        checkinDates: isDateInBlockedDates
            ? [
                  ...availabilityWithUnblockedCheckinDates,
                  ...datesAlreadyInState(
                      [
                          ...passedAvailabilityState.blockedDates,
                          ...passedAvailabilityState.bookedDays
                      ],
                      dateTo ?? dateFrom
                  )
              ]
            : [
                  ...passedAvailabilityState.checkinDates,
                  modifiersForDates.checkIns
              ].filter(
                  date =>
                      ![...mergedBlockedDates, ...blockedDatesInModifiers]
                          .map(date =>
                              format(new Date(date), fullDateWithDashes)
                          )
                          .toString()
                          .includes(format(new Date(date), fullDateWithDashes))
              ),
        blockedDates: isDateInBlockedDates
            ? availabilityWithUnblockDates
            : [
                  ...passedAvailabilityState.blockedDates,
                  ...availabilityWithBlockedDates,
                  ...mergedBlockedDates,
                  ...blockedDatesInModifiers
              ]
    };

    const itrUpdatedData = {
        blockedDates: isDateInBlockedDates
            ? availabilityWithUnblockDates
            : [...rangeOfDates, ...passedAvailabilityState.blockedDates]
    };

    return isStr ? strUpdatedData : itrUpdatedData;
};
